import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Chapters } from 'models'
import { scrollToElement as scrollFc } from 'utils'

const MenuContext = createContext({
  isOpen: false,
  chapters: [] as Chapters,
  visibleSection: '',
  setChapters: (() => {}) as Dispatch<SetStateAction<Chapters>>,
  toggleMenu: () => {},
  closeMenu: () => {},
  scrollToElement: (
    element: HTMLElement,
    alignment?: ScrollLogicalPosition
  ) => {},
})

type Props = {
  children: ReactNode
}

export const MenuContextProvider: FC<Props> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [chapters, setChapters] = useState<Chapters>([])
  const [visibleSections, setVisibleSections] = useState<string[]>([])
  const visibleElements = useRef<string[]>([])
  const observer = useRef<IntersectionObserver>()

  useEffect(() => {
    if (chapters) {
      observer.current = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (document.body.classList.contains('scrolling')) {
                visibleElements.current = [
                  entry.target.getAttribute('data-menu-title') as string,
                  ...visibleElements.current,
                ]
              } else {
                setVisibleSections(s => [
                  entry.target.getAttribute('data-menu-title') as string,
                  ...s,
                ])
              }
            } else {
              if (document.body.classList.contains('scrolling')) {
                const title = entry.target.getAttribute('data-menu-title')
                visibleElements.current = visibleElements.current.filter(
                  t => t !== title
                )
              } else {
                setVisibleSections(s =>
                  s.filter(
                    t =>
                      t !==
                      (entry.target.getAttribute('data-menu-title') as string)
                  )
                )
              }
            }
          })
        },
        { rootMargin: '-49% 0px -49% 0px' }
      )

      chapters
        .map(c => c.sections)
        .flat()
        .forEach(section => {
          observer.current?.observe(section.element)
        })

      return () => {
        observer.current?.disconnect()
      }
    }
  }, [chapters])

  const handleScrollEnd = () => {
    if (visibleElements.current) {
      setVisibleSections(visibleElements.current)
      visibleElements.current = []
    }
  }

  return (
    <MenuContext.Provider
      value={{
        isOpen,
        chapters: chapters.map(c => ({
          ...c,
          sections: c.sections.map(s => ({
            ...s,
            isActive: s.title === visibleSections[0],
          })),
        })),
        setChapters,
        toggleMenu: () => setIsOpen(v => !v),
        closeMenu: () => setIsOpen(false),
        visibleSection: visibleSections[0],
        scrollToElement: (
          element: HTMLElement,
          alignment?: ScrollLogicalPosition
        ) => scrollFc(element, handleScrollEnd, alignment || 'center'),
      }}
    >
      {children}
    </MenuContext.Provider>
  )
}

export const useMenuContext = () => useContext(MenuContext)
